import { TransactionEntity } from "@/modules/finances/entities/transactionEntity"
import type { TransactionDto } from "@/modules/finances/gateway/transactionDto"
import { PaymentsToValidateEnumApi } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateApi"
import { paymentsToValidateKeys } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateKeys"
import { PaymentToValidateFilterStatus } from "@/modules/finances/types/financesType"
import { isValidPaymentsToValidateSortBy, PaymentsToValidateColumnToSort } from "@/modules/finances/utils/financeUtils"
import useDebounce from "@/modules/hooks/useDebounce"
import type { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { isValidSortOrder } from "@/modules/ui/components/dataTables/datatableUtils"
import { isInArray } from "@/modules/utils/isInArray"
import { useQueryParam, useSetMultiParams } from "@/modules/utils/navigation/useQueryParam"
import type { Page } from "@/modules/utils/paginated"
import { useServicesContext } from "@modules/core/services/services.context"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

export const usePaymentsToValidate = (paymentsToValidateEnumApi: PaymentsToValidateEnumApi) => {
  const { paymentsToValidateApi } = useServicesContext()

  const [searchParams] = useSearchParams()
  const setListParams = useSetMultiParams()
  const sortBy = searchParams.get("sortBy") ?? undefined
  const sortOrder = searchParams.get("order") ?? undefined
  const { page, setPage, size: pageSize, setSize: setPageSize } = usePersistedDatatable(20, true)
  const [nameFilter] = useQueryParam("nameFilter")
  const [statusFilter] = useQueryParam("status")

  const debouncedNameFilter = useDebounce(nameFilter ?? undefined, 300)

  const status = isInArray(Object.values(PaymentToValidateFilterStatus), statusFilter) ? statusFilter : undefined

  const { data, isLoading } = useSuspenseQuery({
    queryKey: paymentsToValidateKeys.paymentsToValidate(paymentsToValidateEnumApi, {
      page,
      pageSize,
      sortBy,
      sortOrder,
      nameFilter: debouncedNameFilter,
      status,
    }),

    queryFn: async () => {
      let result: Page<TransactionDto>

      if (sortBy && isValidPaymentsToValidateSortBy(sortBy) && sortOrder && isValidSortOrder(sortOrder)) {
        result = await paymentsToValidateApi.getPaymentsToValidate(
          paymentsToValidateEnumApi,
          status,
          debouncedNameFilter,
          page,
          pageSize,
          sortBy,
          sortOrder,
        )
      } else {
        result = await paymentsToValidateApi.getPaymentsToValidate(
          paymentsToValidateEnumApi,
          status,
          debouncedNameFilter,
          page,
          pageSize,
        )
      }

      return { ...result, content: result.content.map((transaction) => TransactionEntity.fromDto(transaction)) }
    },
  })

  const handlePreviousPage = () => {
    if (data.page > 0) {
      setPage((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (data.page < data.totalPages - 1) {
      setPage((prev) => prev + 1)
    }
  }

  const setSorting = (field: PaymentsToValidateColumnToSort, order: ColumnSortingOrder) => {
    if (field === sortBy && order === sortOrder) {
      setListParams([
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    } else {
      setListParams([
        { key: "sortBy", value: field },
        { key: "order", value: order },
      ])
    }
  }

  return {
    data: data.content,
    isLoading,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage: data.page > 0,
    hasNextPage: data.page < data.totalPages - 1,
    totalElements: data.totalSize,
    setSorting,
    sortBy,
    sortOrder,
  }
}
